import React, { useState } from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { 
  Box, 
  Container, 
  Typography, 
  Button, 
  LinearProgress, 
  Chip,
  Paper,
  Tabs,
  Tab,
} from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FaArrowRight,
  FaRedo
} from 'react-icons/fa';

import { steps } from './data/assessmentSteps';
import { calculateCareerRecommendations } from './utils/careerMatching';
import { CareerResultCard } from './components/CareerResultCard';
import { AssessmentQuestion } from './components/AssessmentQuestion';
import { AnswersSummary } from './components/AnswersSummary';
import { StepAnswers, Answer, CareerRecommendation } from './types';

const theme = createTheme({
  palette: {
    primary: {
      main: '#2196F3',
    },
    secondary: {
      main: '#1976D2',
    },
    background: {
      default: '#f5f5f5',
      paper: '#ffffff',
    },
  },
  typography: {
    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    h1: {
      fontSize: '2.5rem',
      fontWeight: 600,
    },
    h5: {
      fontWeight: 500,
    },
    button: {
      textTransform: 'none',
      fontWeight: 500,
    },
  },
  shape: {
    borderRadius: 8,
  },
});

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`career-tabpanel-${index}`}
      aria-labelledby={`career-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `career-tab-${index}`,
    'aria-controls': `career-tabpanel-${index}`,
  };
}

function App() {
  const [currentStep, setCurrentStep] = useState(0);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [started, setStarted] = useState(false);
  const [answers, setAnswers] = useState<StepAnswers[]>([]);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [showSummary, setShowSummary] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [recommendations, setRecommendations] = useState<CareerRecommendation[]>([]);
  const [selectedTab, setSelectedTab] = useState(0);

  const currentStepData = steps[currentStep];
  const currentQuestionData = currentStepData?.questions[currentQuestion];
  const progress = ((currentStep * 2 + currentQuestion) / (steps.length * 2)) * 100;

  const handleAnswer = (answerIndex: number) => {
    setSelectedAnswer(answerIndex);
    
    // Update answers
    const newAnswers = [...answers];
    if (!newAnswers[currentStep]) {
      newAnswers[currentStep] = {
        stepIndex: currentStep,
        answers: []
      };
    }
    
    const answer: Answer = {
      questionIndex: currentQuestion,
      answerIndex: answerIndex,
      question: currentQuestionData.question,
      answer: currentQuestionData.options[answerIndex]
    };

    const existingAnswerIndex = newAnswers[currentStep].answers.findIndex(
      a => a.questionIndex === currentQuestion
    );

    if (existingAnswerIndex !== -1) {
      newAnswers[currentStep].answers[existingAnswerIndex] = answer;
    } else {
      newAnswers[currentStep].answers.push(answer);
    }

    setAnswers(newAnswers);

    // Add a longer delay before moving to the next question
    setTimeout(() => {
      handleNext();
    }, 500);
  };

  const handleNext = () => {
    // Check if we have more questions in current step
    if (currentQuestion < currentStepData.questions.length - 1) {
      setCurrentQuestion(prev => prev + 1);
      setSelectedAnswer(null);
    } 
    // If no more questions in current step, move to next step
    else if (currentStep < steps.length - 1) {
      setCurrentStep(prev => prev + 1);
      setCurrentQuestion(0);
      setSelectedAnswer(null);
    } 
    // If no more steps, show summary
    else {
      setShowSummary(true);
    }
  };

  const handleBack = () => {
    // If we have previous questions in current step
    if (currentQuestion > 0) {
      setCurrentQuestion(prev => prev - 1);
      // Get the previous answer if it exists
      const prevAnswer = answers[currentStep]?.answers.find(
        a => a.questionIndex === currentQuestion - 1
      );
      setSelectedAnswer(prevAnswer?.answerIndex ?? null);
    } 
    // If we're at first question but have previous steps
    else if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
      // Go to last question of previous step
      const prevStepLastQuestionIndex = steps[currentStep - 1].questions.length - 1;
      setCurrentQuestion(prevStepLastQuestionIndex);
      // Get the previous answer if it exists
      const prevAnswer = answers[currentStep - 1]?.answers.find(
        a => a.questionIndex === prevStepLastQuestionIndex
      );
      setSelectedAnswer(prevAnswer?.answerIndex ?? null);
    }
  };

  const handleShowResults = () => {
    const recommendations = calculateCareerRecommendations(answers);
    setRecommendations(recommendations);
    setShowResults(true);
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
  };

  if (!started) {
    return (
      <ThemeProvider theme={theme}>
        <Container maxWidth="md">
          <Box sx={{ my: 8, textAlign: 'center' }}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <Typography 
                variant="h1" 
                component="h1" 
                gutterBottom
                sx={{ 
                  color: 'text.primary',
                  mb: 3
                }}
              >
                Career Path Assessment
              </Typography>
              
              <Typography 
                variant="h6" 
                sx={{ 
                  mb: 4, 
                  color: 'text.secondary',
                  maxWidth: 600,
                  mx: 'auto'
                }}
              >
                Discover career opportunities aligned with your interests, skills, and aspirations through our comprehensive assessment
              </Typography>

              <Button
                variant="contained"
                size="large"
                onClick={() => setStarted(true)}
                sx={{ 
                  px: 6,
                  py: 1.5,
                  fontSize: '1.1rem'
                }}
              >
                Begin Assessment
              </Button>
            </motion.div>
          </Box>
        </Container>
      </ThemeProvider>
    );
  }

  if (showResults) {
    return (
      <ThemeProvider theme={theme}>
        <Container maxWidth="md">
          <Box sx={{ my: 8 }}>
            <Typography variant="h4" gutterBottom align="center" color="primary" sx={{ mb: 4 }}>
              Your Career Recommendations
            </Typography>
            
            <Typography variant="body1" sx={{ mb: 4 }} align="center">
              Based on your responses, we've analyzed your interests, skills, and aspirations to identify career paths that best align with your profile. Here are your personalized recommendations:
            </Typography>

            <Box sx={{ width: '100%' }}>
              <Paper 
                elevation={3} 
                sx={{ 
                  borderRadius: 2,
                  overflow: 'hidden'
                }}
              >
                <Tabs 
                  value={selectedTab} 
                  onChange={handleTabChange}
                  variant="scrollable"
                  scrollButtons="auto"
                  sx={{
                    borderBottom: 1,
                    borderColor: 'divider',
                    bgcolor: theme.palette.primary.main,
                    '& .MuiTab-root': {
                      color: 'rgba(255, 255, 255, 0.7)',
                      '&.Mui-selected': {
                        color: '#fff',
                      },
                    },
                  }}
                >
                  {recommendations.map((rec, index) => (
                    <Tab 
                      key={index}
                      label={`${rec.title} (${rec.matchPercentage}%)`}
                      {...a11yProps(index)}
                    />
                  ))}
                </Tabs>

                {recommendations.map((rec, index) => (
                  <TabPanel key={index} value={selectedTab} index={index}>
                    <CareerResultCard 
                      key={index}
                      recommendation={rec}
                    />
                  </TabPanel>
                ))}
              </Paper>
              
              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
                <Button 
                  variant="contained" 
                  color="primary" 
                  onClick={() => {
                    setShowResults(false);
                    setShowSummary(false);
                    setCurrentStep(0);
                    setCurrentQuestion(0);
                    setAnswers([]);
                    setStarted(false);
                  }}
                  sx={{ 
                    minWidth: 200,
                    py: 1
                  }}
                >
                  Start Over
                </Button>
              </Box>
            </Box>
          </Box>
        </Container>
      </ThemeProvider>
    );
  }

  if (showSummary) {
    return (
      <ThemeProvider theme={theme}>
        <Container maxWidth="md">
          <AnswersSummary
            steps={steps}
            answers={answers}
            onBack={() => {
              setShowSummary(false);
              setCurrentStep(steps.length - 1);
              setCurrentQuestion(steps[steps.length - 1].questions.length - 1);
            }}
            onShowResults={handleShowResults}
          />
        </Container>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <Container maxWidth="md">
        <Box sx={{ my: 4 }}>
          <LinearProgress 
            variant="determinate" 
            value={progress} 
            sx={{ mb: 4, height: 6, borderRadius: 3 }}
            className="progress-bar"
          />
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
            <Chip 
              icon={<currentStepData.icon size={20} />}
              label={currentStepData.title}
              sx={{ 
                bgcolor: currentStepData.color,
                color: 'white',
                fontSize: '1rem',
                py: 2,
                px: 1
              }}
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                startIcon={<FaArrowRight style={{ transform: 'rotate(180deg)' }} />}
                onClick={handleBack}
                variant="outlined"
                size="small"
                disabled={currentStep === 0 && currentQuestion === 0}
              >
                Back
              </Button>
              <Button
                startIcon={<FaRedo size={16} />}
                onClick={() => {
                  setCurrentStep(0);
                  setCurrentQuestion(0);
                  setAnswers([]);
                  setStarted(false);
                }}
                variant="outlined"
                size="small"
              >
                Restart Assessment
              </Button>
            </Box>
          </Box>

          <AnimatePresence mode='wait'>
            <AssessmentQuestion
              question={currentQuestionData}
              selectedAnswer={selectedAnswer}
              onAnswer={handleAnswer}
              color={currentStepData.color}
            />
          </AnimatePresence>
        </Box>
      </Container>
    </ThemeProvider>
  );
}

export default App;
